import * as React from 'react';
import {Helmet} from 'react-helmet';
import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {useMutation} from '@apollo/react-hooks';

import type {FilterObject} from 'nutshell-core/types';

import {headTitle} from 'shells/document/head-title';
import {ErrorState} from 'shells/error-state/error-state';

import type {
    EditionUpdateInput,
    EditionFragment as DripEdition,
    EditionStubFragment as DripEditionStub,
    EditionTestEmailSend,
    EditionTestEmailSendVariables,
} from 'nutshell-graphql-types';

import {
    generateUpdatedSearch,
    updateFilterValues,
} from '../../../reports/generate-url-updating-param';
import type {ReportURLParams} from '../../../reports/report-types';

import {appHistory} from '../../../../history';

import EDITION_TEST_EMAIL_SEND from '../../editions/graphql/mutations/edition-test-email-send.graphql';
import {SavingDebounce} from '../../editions/utils/saving-debounce';
import {DripSequenceEditionMutations} from '../../graphql-wrappers/drip-sequence-edition/drip-sequence-edition-mutations';
import {DripSequenceEditionQuery} from '../../graphql-wrappers/drip-sequence-edition/drip-sequence-edition-query';
import {DripSequenceEditionStubsQuery} from '../../graphql-wrappers/drip-sequence-edition/drip-sequence-edition-stubs-query';

import {DripSequenceEditionPage} from './drip-sequence-edition-page';

type OwnProps = ContextRouter;

type Props = OwnProps & {
    edition: DripEdition | null | undefined;
    editionsList: DripEditionStub[];
    editionStub: DripEditionStub | null | undefined;
    errorLoadingEdition: string | null | undefined;
    errorLoadingEditionsList: string | null | undefined;
    isLoading: boolean;
    isLoadingEditionsList: boolean;
    isSaving: boolean;
    onChange: (editionId: string, beeBodyJson: string) => Promise<any>;
    onCreateEdition: (name: string, campaignId: string) => Promise<any>;
    onSendEmail: (toEmailAddress: string[]) => Promise<any>;
    onDuplicateEdition: (editionId: string, campaignId: string, name: string) => Promise<any>;
    onEditionSave: (saveData: EditionUpdateInput) => Promise<any>;
    dripSequenceId: string;
    editionId: string;
    updateParams: (activeTab: string, newEditionId: string) => void;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onRemoveAllFilters: () => void;
};

function DripSequenceEditionPageWrapperComponent(props: Props) {
    const pageTitle = props.editionStub
        ? `${props.editionStub.campaign.name} • ${props.editionStub.name}`
        : 'Drip sequence message';

    const onEditionChange = (activeTab: string, newEditionId: string) => {
        const keys = [
            'sentEditions',
            'viewedEditions',
            'clickedEditions',
            'bouncedEditions',
            'unsubscribedEditions',
        ];

        // update urlParams filter keys with newEditionId
        const newFilter = updateFilterValues(props.urlParams.filter, keys, newEditionId);

        const getNewSearch = generateUpdatedSearch(props.urlParams);

        // get new search string using the updated filter
        const newSearch = getNewSearch(
            {
                filter: newFilter,
            },
            true
        ); // These filters need to be merged

        // update the path with the newEditionId
        const newPath = generatePath(props.match.path, {
            dripSequenceId: props.dripSequenceId,
            dripSequenceEditionId: newEditionId,
            tab: activeTab,
        });

        // navigate to the new path with new search string
        props.history.replace({pathname: newPath, search: newSearch});
    };

    return (
        <>
            <Helmet>
                <title>{headTitle(pageTitle)}</title>
            </Helmet>
            {props.errorLoadingEdition ? (
                <ErrorState errorMessage={props.errorLoadingEdition} /> // TODO: Make sure this is showing up correctly.
            ) : (
                <DripSequenceEditionPage
                    {...props}
                    onSetSelectedEditorTab={(newTab: string) => {
                        props.updateParams(newTab, props.editionId);
                    }}
                    onEditionChange={(activeTab: string, newEditionId: string) =>
                        onEditionChange(activeTab, newEditionId)
                    }
                    generatePath={(newTab: string) => {
                        const path = generatePath(props.match.path, {
                            dripSequenceId: props.dripSequenceId,
                            dripSequenceEditionId: props.editionId,
                            tab: newTab,
                        });

                        return props.location.search && props.location.search.length
                            ? `${path}${props.location.search}`
                            : path;
                    }}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                />
            )}
        </>
    );
}

export function DripSequenceEditionPageWrapper(props: OwnProps) {
    const dripSequenceIdParam = props.match.params.dripSequenceId;
    const dripSequenceApiId = dripSequenceIdParam ? dripSequenceIdParam : '';

    const editionIdParam = props.match.params.dripSequenceEditionId;
    const editionApiId = editionIdParam ? editionIdParam : '';

    const [sendTestEmail] = useMutation<EditionTestEmailSend, EditionTestEmailSendVariables>(
        EDITION_TEST_EMAIL_SEND
    );

    function updateParams(tab: string, editionId: string) {
        const newPath = generatePath(props.match.path, {
            dripSequenceEditionId: editionId,
            dripSequenceId: dripSequenceApiId,
            tab: tab,
        });

        // Make sure to pass the search here, because without it we force a
        // rerender, which causes BEE to reload.
        props.history.replace({pathname: newPath, search: props.location.search});
    }

    return (
        <DripSequenceEditionStubsQuery dripSequenceId={dripSequenceApiId}>
            {({
                editions,
                isLoading: isLoadingEditionsList,
                error: errorLoadingEditionsList,
                refetch: refetchEditionsList,
            }) => (
                <DripSequenceEditionQuery editionId={editionApiId}>
                    {({
                        edition,
                        editionStub,
                        isLoading: isLoadingEdition,
                        error: errorLoadingEdition,
                    }) => (
                        <DripSequenceEditionMutations>
                            {({onUpdate, onCreate, onDuplicate}) => (
                                <SavingDebounce
                                    onSave={(saveData) => {
                                        return onUpdate(saveData);
                                    }}
                                >
                                    {({onChange, isSaving, hasChanged}) => (
                                        <DripSequenceEditionPageWrapperComponent
                                            {...props}
                                            edition={edition}
                                            editionsList={editions}
                                            editionStub={editionStub}
                                            errorLoadingEdition={errorLoadingEdition}
                                            errorLoadingEditionsList={errorLoadingEditionsList}
                                            isLoading={isLoadingEdition || isLoadingEditionsList}
                                            isLoadingEditionsList={isLoadingEditionsList}
                                            isSaving={isSaving || hasChanged}
                                            onChange={(editionId: string, beeBodyJson: string) => {
                                                onChange({editionId, beeBodyJson});
                                            }}
                                            // @ts-expect-error
                                            onCreateEdition={(name, campaignId) =>
                                                onCreate(name, campaignId).then((newEdition) => {
                                                    // Refetch the editions list for the mini map
                                                    refetchEditionsList();

                                                    if (newEdition)
                                                        appHistory.push(newEdition.htmlUrlPath);
                                                })
                                            }
                                            // @ts-expect-error
                                            onDuplicateEdition={(editionId, campaignId, name) =>
                                                onDuplicate(editionId, campaignId, name).then(
                                                    (newEdition) => {
                                                        // Refetch the editions list for the mini map
                                                        refetchEditionsList();

                                                        if (newEdition)
                                                            appHistory.push(newEdition.htmlUrlPath);
                                                    }
                                                )
                                            }
                                            // @ts-expect-error
                                            onSendEmail={(emailAddresses) => {
                                                const editionId = edition && edition.id;
                                                if (editionId) {
                                                    return sendTestEmail({
                                                        variables: {
                                                            input: {
                                                                editionId: editionId,
                                                                emailAddresses: emailAddresses,
                                                            },
                                                        },
                                                    });
                                                }
                                            }}
                                            onEditionSave={onUpdate}
                                            dripSequenceId={dripSequenceApiId}
                                            editionId={editionApiId}
                                            updateParams={updateParams}
                                        />
                                    )}
                                </SavingDebounce>
                            )}
                        </DripSequenceEditionMutations>
                    )}
                </DripSequenceEditionQuery>
            )}
        </DripSequenceEditionStubsQuery>
    );
}
